import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, CalendarClock, Save, User, UserPlus, Users } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { showError, showSuccess } from '@/lib/swal'
import { config } from '@/core/config'
import { employeesService } from '@/services/employeesService'
import { hireEmployeeRequestService } from '@/services/hireEmployeeRequestService'
import { useAuthStore } from '@/store/auth.store'
import type {
  HireEmployeeApprovalUser,
  HireEmployeeRequestByIdResponse,
  HireEmployeeRequestCreatePayload,
} from '../types'
import { newHireEmployeeRequest } from '../types'

const arabicInputStyle = {
  fontFamily: 'Calibri, "Noto Naskh Arabic", "Segoe UI", Tahoma, Arial, sans-serif',
} as const

function pickStr(r: Record<string, unknown>, camel: string, pascal: string): string {
  const v = r[camel] ?? r[pascal]
  return v != null && v !== '' ? String(v) : ''
}

/** Same pattern as inventory items: relative upload paths need API origin (see itemService.resolveAttachmentUrl). */
function resolveAttachmentPreviewUrl(pathOrUrl: string | null | undefined): string | null {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

function formatActionDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function actionBadgeVariant(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const a = action.toLowerCase()
  if (a.includes('reject')) return 'destructive'
  if (a.includes('approv') && !a.includes('pending')) return 'default'
  if (a.includes('pending')) return 'secondary'
  return 'outline'
}

/** Hide raw EMP-#### - prefix from API userName */
function stripEmpCodePrefix(userName: string): string {
  return String(userName || '').replace(/^EMP-\d+\s*-\s*/i, '').trim()
}

/** Collapse accidental duplicate runs e.g. "Hamid Anjum Hamid Anjum" */
function dedupeConsecutiveWords(s: string): string {
  const parts = s.split(/\s+/).filter(Boolean)
  if (parts.length < 2) return s.trim()
  const out: string[] = [parts[0]]
  for (let i = 1; i < parts.length; i++) {
    if (parts[i] !== parts[i - 1]) out.push(parts[i])
  }
  return out.join(' ')
}

function fallbackApproverLabel(userName: string): string {
  return dedupeConsecutiveWords(stripEmpCodePrefix(userName)) || '—'
}

function initialsFromName(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean)
  const a = parts[0]?.charAt(0) ?? ''
  const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : parts[0]?.charAt(1) ?? ''
  const s = (a + b).toUpperCase()
  return s || '?'
}

export const HireEmployeeDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const userId = user?.userId ?? 0

  const requestId = useMemo(() => Number(params.id ?? 0), [params.id])
  const isNew = !requestId || Number.isNaN(requestId)
  const isViewMode = searchParams.get('view') === '1'
  const readOnly = isViewMode && !isNew

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isEmployeeCreated, setIsEmployeeCreated] = useState(false)

  const [approversOpen, setApproversOpen] = useState(false)
  const [approversLoading, setApproversLoading] = useState(false)
  const [approvers, setApprovers] = useState<HireEmployeeApprovalUser[]>([])
  const [approverEmpNames, setApproverEmpNames] = useState<Record<number, string>>({})

  const [doc, setDoc] = useState<HireEmployeeRequestCreatePayload>(() => newHireEmployeeRequest())
  const req = t('hireEmployeePage.requiredMark', { defaultValue: '*' })

  const mapToPayload = (r: HireEmployeeRequestByIdResponse): HireEmployeeRequestCreatePayload => {
    const raw = r as unknown as Record<string, unknown>
    return {
      id: r.id ?? requestId ?? 0,
      userId: userId || 0,
      jobId: r.jobId ?? 0,
      empFirstNameEn: pickStr(raw, 'empFirstNameEn', 'EmpFirstNameEn'),
      empFatherNameEn: pickStr(raw, 'empFatherNameEn', 'EmpFatherNameEn'),
      empGrandNameEn: pickStr(raw, 'empGrandNameEn', 'EmpGrandNameEn'),
      empFamilyNameEn: pickStr(raw, 'empFamilyNameEn', 'EmpFamilyNameEn'),
      empFirstNameAr: pickStr(raw, 'empFirstNameAr', 'EmpFirstNameAr'),
      empFatherNameAr: pickStr(raw, 'empFatherNameAr', 'EmpFatherNameAr'),
      empGrandNameAr: pickStr(raw, 'empGrandNameAr', 'EmpGrandNameAr'),
      empFamilyNameAr: pickStr(raw, 'empFamilyNameAr', 'EmpFamilyNameAr'),
      position: r.position ?? '',
      salaryRangeFrom: Number(r.salaryRangeFrom ?? 0),
      salaryRangeTo: Number(r.salaryRangeTo ?? 0),
      trainingCourses: String(r.trainingCourses ?? ''),
      yearsOfExperience: Number(r.yearsOfExperience ?? 0),
      details: String(r.details ?? ''),
      otherRequiredQualification: String(r.otherRequiredQualification ?? ''),
      educationalQualification: String(r.educationalQualification ?? ''),
      hireEmpImg: null,
      branchId: branchId || 0,
    }
  }

  useEffect(() => {
    if (!branchId) return

    const load = async () => {
      try {
        setLoading(true)
        if (isNew) {
          setDoc({
            ...newHireEmployeeRequest(),
            branchId: branchId || 0,
            userId: userId || 0,
          })
          setImagePreview(null)
          setIsEmployeeCreated(false)
          return
        }

        const raw = await hireEmployeeRequestService.getById(requestId, branchId)
        const res =
          raw && typeof raw === 'object' && 'data' in raw && (raw as { data: unknown }).data != null
            ? (raw as { data: HireEmployeeRequestByIdResponse }).data
            : (raw as HireEmployeeRequestByIdResponse)
        const resRaw = res as unknown as Record<string, unknown>
        const created =
          Boolean(resRaw.isEmployeeCreated) ||
          Boolean(resRaw.IsEmployeeCreated) ||
          Boolean(resRaw.isCreatedEmployee) ||
          Boolean(resRaw.IsCreatedEmployee)
        setIsEmployeeCreated(created)
        setDoc(mapToPayload(res))
        const attRaw = String(
          (res as unknown as Record<string, unknown>).attachmentUrl ??
            (res as unknown as Record<string, unknown>).AttachmentUrl ??
            ''
        ).trim()
        setImagePreview(resolveAttachmentPreviewUrl(attRaw))
      } catch (e: any) {
        const msg = e?.response?.data?.message ?? e?.message ?? t('hireEmployeePage.loadFailed')
        toast.error(String(msg))
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [isNew, branchId, requestId, userId, t])

  const loadApprovers = useCallback(async () => {
    if (!requestId) return
    try {
      setApproversLoading(true)
      const rows = await hireEmployeeRequestService.getApprovalUsers(requestId)
      setApprovers(rows)
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ??
          e?.message ??
          t('hireEmployeePage.approversLoadFailed', { defaultValue: 'Failed to load approvers.' })
      )
      setApprovers([])
    } finally {
      setApproversLoading(false)
    }
  }, [requestId, t])

  const openApproversModal = useCallback(() => {
    setApproversOpen(true)
    void loadApprovers()
  }, [loadApprovers])

  useEffect(() => {
    if (!approversOpen || !branchId || approvers.length === 0) {
      if (!approversOpen) setApproverEmpNames({})
      return
    }
    const ids = [...new Set(approvers.map((a) => a.empId).filter((id) => id > 0))]
    if (ids.length === 0) {
      setApproverEmpNames({})
      return
    }
    let cancelled = false
    void (async () => {
      const next: Record<number, string> = {}
      await Promise.all(
        ids.map(async (empId) => {
          try {
            const emp = await employeesService.getEmployeeById(branchId, empId)
            const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()
            next[empId] = name || emp.empFirstNameAr || emp.empCode || ''
          } catch {
            next[empId] = ''
          }
        })
      )
      if (!cancelled) setApproverEmpNames(next)
    })()
    return () => {
      cancelled = true
    }
  }, [approversOpen, branchId, approvers])

  const setField = <K extends keyof HireEmployeeRequestCreatePayload>(key: K, value: HireEmployeeRequestCreatePayload[K]) => {
    setDoc((prev) => ({ ...prev, [key]: value }))
  }

  const profileInitials = useMemo(() => {
    const a = doc.empFirstNameEn?.trim().charAt(0) || ''
    const b = doc.empFamilyNameEn?.trim().charAt(0) || ''
    const s = (a + b).toUpperCase()
    return s || '?'
  }, [doc.empFirstNameEn, doc.empFamilyNameEn])

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setField('hireEmpImg', file)
      const reader = new FileReader()
      reader.onloadend = () => setImagePreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const validate = async () => {
    if (!doc.empFirstNameEn.trim()) {
      return showError(
        t('hireEmployeePage.saveFailedTitle'),
        t('hireEmployeePage.toastFirstNameEn', { defaultValue: 'First name (English) is required.' })
      )
    }
    if (!doc.position.trim()) return showError(t('hireEmployeePage.saveFailedTitle'), t('hireEmployeePage.toastPosition'))
    if (!branchId) return showError(t('hireEmployeePage.saveFailedTitle'), t('hireEmployeePage.toastBranchRequired', { defaultValue: 'Please select a branch first.' }))
    if (!userId) return showError(t('hireEmployeePage.saveFailedTitle'), t('hireEmployeePage.toastUserRequired', { defaultValue: 'UserId is required.' }))
    return null
  }

  const handleSave = async () => {
    const err = await validate()
    if (err) return
    try {
      setSaving(true)
      const payload: HireEmployeeRequestCreatePayload = {
        ...doc,
        id: isNew ? 0 : requestId,
        branchId,
        userId,
      }
      await hireEmployeeRequestService.createRequest(payload)
      await showSuccess(t('hireEmployeePage.savedTitle'), t('hireEmployeePage.savedMessage'), 2500)
      navigate('/self-services/hire-employee')
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? t('hireEmployeePage.saveFailedMessage')
      await showError(t('hireEmployeePage.saveFailedTitle'), msg)
    } finally {
      setSaving(false)
    }
  }

  const formDisabled = saving || loading || readOnly

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('hireEmployeePage.pageTitle')}
        description={
          readOnly
            ? t('hireEmployeePage.viewDescription', { defaultValue: 'Read-only request details.' })
            : t('hireEmployeePage.pageDescription')
        }
      >
        {readOnly ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="default"
              className="gap-2"
              disabled={loading || isEmployeeCreated}
              title={
                isEmployeeCreated
                  ? t('hireEmployeePage.insertEmployeeDisabledHint', {
                      defaultValue: 'An employee has already been created from this request.',
                    })
                  : undefined
              }
              onClick={() => navigate(`/employees/new?hireRequestId=${requestId}`)}
            >
              <UserPlus className="h-4 w-4" />
              {t('hireEmployeePage.insertEmployee', { defaultValue: 'Insert employee' })}
            </Button>
            <Button type="button" variant="outline" className="gap-2" onClick={openApproversModal}>
              <Users className="h-4 w-4" />
              {t('hireEmployeePage.viewApprovers', { defaultValue: 'View Approvers' })}
            </Button>
          </div>
        ) : null}
      </PageHeader>

      <Dialog open={approversOpen} onOpenChange={setApproversOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-1.25rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl border-0 p-0 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 sm:max-w-4xl">
          <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-primary/[0.11] via-primary/[0.04] to-background px-8 pb-6 pt-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/[0.07] blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-1/4 h-32 w-64 rounded-full bg-primary/[0.05] blur-3xl" />
            <DialogHeader className="relative space-y-3 text-left">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner ring-1 ring-primary/10">
                    <Users className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <div className="space-y-1">
                    <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
                      {t('hireEmployeePage.approversModalTitle', { defaultValue: 'Approval chain' })}
                    </DialogTitle>
                    <DialogDescription className="max-w-lg text-sm leading-relaxed text-muted-foreground">
                      {t('hireEmployeePage.approversModalDescription', {
                        defaultValue: 'Users assigned to approve this hire request and their latest action.',
                      })}
                    </DialogDescription>
                  </div>
                </div>
                {!approversLoading && approvers.length > 0 ? (
                  <div className="rounded-full border border-primary/15 bg-background/80 px-4 py-1.5 text-xs font-medium tabular-nums text-primary shadow-sm backdrop-blur-sm">
                    {t('hireEmployeePage.approversCount', {
                      count: approvers.length,
                      defaultValue:
                        approvers.length === 1 ? '{{count}} approver' : '{{count}} approvers',
                    })}
                  </div>
                ) : null}
              </div>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scroll-smooth overscroll-y-contain bg-gradient-to-b from-muted/20 to-background px-6 py-6 sm:px-8">
            {approversLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20">
                <div className="h-11 w-11 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                <p className="text-sm font-medium text-muted-foreground">
                  {t('hireEmployeePage.approversLoading', { defaultValue: 'Loading approvers…' })}
                </p>
              </div>
            ) : approvers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 py-16 text-center">
                <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">
                  {t('hireEmployeePage.approversEmpty', { defaultValue: 'No approvers found for this request.' })}
                </p>
              </div>
            ) : (
              <ol className="relative space-y-5 pb-1">
                {approvers.map((row, index) => {
                  const resolved =
                    row.empId > 0 && approverEmpNames[row.empId]?.trim()
                      ? approverEmpNames[row.empId].trim()
                      : ''
                  const display = resolved || fallbackApproverLabel(row.userName)
                  const initials = initialsFromName(display)
                  const isLast = index === approvers.length - 1
                  return (
                    <li key={row.id} className="relative">
                      {!isLast ? (
                        <div
                          className="absolute left-[1.375rem] top-[4.25rem] z-0 h-[calc(100%+0.25rem)] w-px bg-gradient-to-b from-primary/25 via-border to-transparent"
                          aria-hidden
                        />
                      ) : null}
                      <div className="relative z-[1] overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md">
                        <div className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-primary via-primary/70 to-primary/30" />
                        <div className="p-5 pl-6 sm:p-6 sm:pl-7">
                          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex min-w-0 gap-4">
                              <Avatar className="h-14 w-14 shrink-0 border-2 border-background shadow-md ring-2 ring-primary/15 ring-offset-2 ring-offset-card">
                                <AvatarFallback className="bg-gradient-to-br from-primary/15 to-primary/5 text-base font-semibold text-primary">
                                  {initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 space-y-2 pt-0.5">
                                <p className="text-lg font-semibold leading-tight tracking-tight text-foreground">
                                  {display}
                                </p>
                                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Building2 className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                                  <span className="truncate">{row.departmentName?.trim() || '—'}</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                              <Badge
                                variant={actionBadgeVariant(row.action)}
                                className="rounded-full px-3 py-0.5 text-xs font-semibold shadow-sm"
                              >
                                {row.action?.trim() || '—'}
                              </Badge>
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                                <CalendarClock className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                                {formatActionDate(row.actionDate)}
                              </span>
                            </div>
                          </div>
                          {(row.approvalNote ?? '').trim() ? (
                            <div className="mt-5 rounded-xl border border-border/50 bg-muted/35 px-4 py-3 text-sm leading-relaxed text-foreground/90 backdrop-blur-[2px]">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('hireEmployeePage.approverColNote', { defaultValue: 'Note' })}
                              </span>
                              <span className="break-words">{row.approvalNote}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (readOnly) return
          void handleSave()
        }}
      >
        <div className="space-y-6">
          {/* Profile photo — same pattern as Employees form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('hireEmployeePage.sectionProfilePhoto', { defaultValue: 'Profile Photo' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                <Avatar className="h-24 w-24 shrink-0 bg-muted">
                  <AvatarImage
                    src={imagePreview || undefined}
                    alt=""
                    className="h-full w-full object-contain object-center"
                  />
                  <AvatarFallback className="text-2xl">{profileInitials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Label htmlFor="hireProfileImage">
                    {t('hireEmployeePage.labelUploadProfileImage', { defaultValue: 'Upload profile image' })}
                  </Label>
                  <Input
                    id="hireProfileImage"
                    type="file"
                    accept="image/*"
                    disabled={formDisabled}
                    className="mt-2"
                    onChange={handleProfileImageChange}
                  />
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('hireEmployeePage.hintProfileImage', { defaultValue: 'Select an image for the candidate profile.' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* English name — one row */}
          <Card>
            <CardHeader>
              <CardTitle>{t('hireEmployeePage.sectionNameEn', { defaultValue: 'Name (English)' })}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="empFirstNameEn">
                    {req} {t('hireEmployeePage.labelFirstNameEn', { defaultValue: 'First name (English)' })}
                  </Label>
                  <Input
                    id="empFirstNameEn"
                    value={doc.empFirstNameEn}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFirstNameEn', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empFatherNameEn">{t('hireEmployeePage.labelFatherNameEn', { defaultValue: 'Father name (English)' })}</Label>
                  <Input
                    id="empFatherNameEn"
                    value={doc.empFatherNameEn}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFatherNameEn', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empGrandNameEn">{t('hireEmployeePage.labelGrandNameEn', { defaultValue: 'Grand name (English)' })}</Label>
                  <Input
                    id="empGrandNameEn"
                    value={doc.empGrandNameEn}
                    disabled={formDisabled}
                    onChange={(e) => setField('empGrandNameEn', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empFamilyNameEn">{t('hireEmployeePage.labelFamilyNameEn', { defaultValue: 'Family name (English)' })}</Label>
                  <Input
                    id="empFamilyNameEn"
                    value={doc.empFamilyNameEn}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFamilyNameEn', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arabic name — same label alignment as English; inputs stay RTL for typing */}
          <Card>
            <CardHeader>
              <CardTitle>{t('hireEmployeePage.sectionNameAr', { defaultValue: 'Name (Arabic)' })}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="empFirstNameAr">{t('hireEmployeePage.labelFirstNameAr', { defaultValue: 'First name (Arabic)' })}</Label>
                  <Input
                    id="empFirstNameAr"
                    dir="rtl"
                    style={arabicInputStyle}
                    className="text-right"
                    value={doc.empFirstNameAr}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFirstNameAr', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empFatherNameAr">{t('hireEmployeePage.labelFatherNameAr', { defaultValue: 'Father name (Arabic)' })}</Label>
                  <Input
                    id="empFatherNameAr"
                    dir="rtl"
                    style={arabicInputStyle}
                    className="text-right"
                    value={doc.empFatherNameAr}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFatherNameAr', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empGrandNameAr">{t('hireEmployeePage.labelGrandNameAr', { defaultValue: 'Grand name (Arabic)' })}</Label>
                  <Input
                    id="empGrandNameAr"
                    dir="rtl"
                    style={arabicInputStyle}
                    className="text-right"
                    value={doc.empGrandNameAr}
                    disabled={formDisabled}
                    onChange={(e) => setField('empGrandNameAr', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empFamilyNameAr">{t('hireEmployeePage.labelFamilyNameAr', { defaultValue: 'Family name (Arabic)' })}</Label>
                  <Input
                    id="empFamilyNameAr"
                    dir="rtl"
                    style={arabicInputStyle}
                    className="text-right"
                    value={doc.empFamilyNameAr}
                    disabled={formDisabled}
                    onChange={(e) => setField('empFamilyNameAr', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Remaining request fields */}
          <Card>
            <CardContent className="space-y-6 overflow-visible p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{req} {t('hireEmployeePage.labelPosition')}</Label>
                  <Input
                    value={doc.position}
                    disabled={formDisabled}
                    onChange={(e) => setField('position', e.target.value)}
                    placeholder={t('hireEmployeePage.placeholderPosition')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('hireEmployeePage.labelSalaryRangeFrom', { defaultValue: 'Salary Range From' })}</Label>
                  <Input
                    type="number"
                    value={String(doc.salaryRangeFrom ?? 0)}
                    disabled={formDisabled}
                    onChange={(e) => setField('salaryRangeFrom', Number(e.target.value || 0))}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('hireEmployeePage.labelSalaryRangeTo', { defaultValue: 'Salary Range To' })}</Label>
                  <Input
                    type="number"
                    value={String(doc.salaryRangeTo ?? 0)}
                    disabled={formDisabled}
                    onChange={(e) => setField('salaryRangeTo', Number(e.target.value || 0))}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('hireEmployeePage.labelTrainingCourses', { defaultValue: 'Training Courses' })}</Label>
                  <Input
                    value={doc.trainingCourses}
                    disabled={formDisabled}
                    onChange={(e) => setField('trainingCourses', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('hireEmployeePage.labelYearsOfExperience', { defaultValue: 'Years Of Experience' })}</Label>
                  <Input
                    type="number"
                    value={String(doc.yearsOfExperience ?? 0)}
                    disabled={formDisabled}
                    onChange={(e) => setField('yearsOfExperience', Number(e.target.value || 0))}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('hireEmployeePage.labelEducationalQualification', { defaultValue: 'Educational Qualification' })}</Label>
                  <Input
                    value={doc.educationalQualification}
                    disabled={formDisabled}
                    onChange={(e) => setField('educationalQualification', e.target.value)}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{t('hireEmployeePage.labelOtherRequiredQualification', { defaultValue: 'Other Required Qualification' })}</Label>
                  <Input
                    value={doc.otherRequiredQualification}
                    disabled={formDisabled}
                    onChange={(e) => setField('otherRequiredQualification', e.target.value)}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{t('hireEmployeePage.labelDetails', { defaultValue: 'Details' })}</Label>
                  <Textarea
                    value={doc.details}
                    disabled={formDisabled}
                    onChange={(e) => setField('details', e.target.value)}
                    rows={5}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving || loading}
                  onClick={() => navigate('/self-services/hire-employee')}
                >
                  {t('hireEmployeePage.return')}
                </Button>

                {!readOnly ? (
                  <Button type="submit" disabled={saving || loading}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving
                      ? t('hireEmployeePage.saving', { defaultValue: 'Saving...' })
                      : !isNew
                        ? t('common.update')
                        : t('hireEmployeePage.save')}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  )
}
